import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  AssistantLinkTarget,
  ProposeActionConfirm,
  ProposeActionDisplay,
} from '@core/tellent-assistant-types';
import { ButtonComponent, IconComponent, type IconName } from '@core/user-interface-legacy';

import { TellentAssistantLinkDirective } from '../../link/assistant-link.directive';

/** A resolved link to the resource the executed action created/affected. */
export interface ProposalResultLink {
  label: string;
  url: string;
  target?: AssistantLinkTarget;
}

const VALUE_LIMIT = 280;

export interface DisplayProperty {
  /** Stable identity for `@for` tracking — the argument name each row derives from. */
  key: string;
  label: string;
  value: string;
  /** When true, `value` is HTML the template renders (sanitized) instead of as text. */
  html?: boolean;
}

// Disaster-recovery label only — the model authors real labels in the user's
// language via each `display.params` entry's `name`.
function humanizeKey(key: string): string {
  const words = key.replace(/_(html|json)$/, '').replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value);
}

function clamp(text: string): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  return collapsed.length > VALUE_LIMIT ? `${collapsed.slice(0, VALUE_LIMIT)}…` : collapsed;
}

// Multi-line values (a message body) render in full; single-line ones are
// collapsed and clamped. Length alone never forces a block — it only feeds the
// clamp above, so a pathologically long single line can't blow up the card.
function isMultilineValue(text: string): boolean {
  return text.includes('\n');
}

function toProperty(
  key: string,
  label: string,
  raw: string,
  full: boolean,
  html = false,
): DisplayProperty[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  return [{ key, label, value: full ? trimmed : clamp(trimmed), ...(html ? { html: true } : {}) }];
}

/**
 * The card's shown rows. When the model authored `display.params`, each entry
 * is one row (label = `name`, value = `display` or the raw arg verbatim). With
 * no authored presentation, every arg is surfaced humanized so nothing sent is
 * hidden.
 */
export function buildDisplayProperties(
  display: ProposeActionDisplay | null,
  args: Record<string, unknown>,
): DisplayProperty[] {
  const params = display?.params;
  if (params && params.length > 0) {
    return params.flatMap(param => {
      // An html field previews the raw arg verbatim (what's actually sent), so
      // formatting shows — the model's plain-text `display` can't convey it.
      const html = param.format === 'html';
      const raw = html ? toText(args[param.key]) : (param.display ?? toText(args[param.key]));
      const full = html || param.format === 'multiline' || isMultilineValue(raw);
      return toProperty(param.key, param.name, raw, full, html);
    });
  }
  return Object.entries(args).flatMap(([key, value]) => {
    if (value == null || value === '') return [];
    const raw = toText(value);
    return toProperty(key, humanizeKey(key), raw, isMultilineValue(raw));
  });
}

/**
 * A proposed action rendered inline in the assistant message: a preview of
 * WHAT will happen, with the decision (skip / confirm) built into the card
 * footer. Once confirmed and executed, the footer collapses into a muted
 * approved row and a success banner appears under the card, linking to the
 * created resource when the action declared one.
 */
@Component({
  selector: 'tellent-assistant-proposal-preview',
  imports: [ButtonComponent, IconComponent, TellentAssistantLinkDirective, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="card">
      <header class="header">
        <rt-icon class="header-icon" [name]="icon()" />
        <span class="header-label">{{ label() }}</span>
      </header>
      <div class="content">
        @if (properties().length > 0) {
          <dl class="properties">
            @for (property of properties(); track property.key) {
              <div class="property">
                <dt class="property-label">{{ property.label }}</dt>
                @if (property.html) {
                  <dd class="property-value is-html" [innerHTML]="property.value"></dd>
                } @else {
                  <dd class="property-value">{{ property.value }}</dd>
                }
              </div>
            }
          </dl>
        }
      </div>

      @if (auto()) {
        <footer class="outcome">
          <rt-icon class="outcome-icon" name="check" />
          <span>{{ doneText() }}</span>
          <span class="outcome-hint">
            · {{ 'tlc.tellent_assistant.proposal.automatic' | translate }}
          </span>
        </footer>
      } @else if (resolvedStatus() || !superseded()) {
        <footer class="decision">
          @switch (resolvedStatus()) {
            @case ('confirmed') {
              <div class="resolved">
                <rt-icon class="resolved-icon" name="check" />
                <span>{{ approvedText() }}</span>
              </div>
            }
            @case ('cancelled') {
              <div class="resolved">
                <span>{{ 'tlc.tellent_assistant.proposal.skipped' | translate }}</span>
              </div>
            }
            @default {
              <h4 class="question">{{ question() }}</h4>
              @if (subtitle()) {
                <p class="subtitle">{{ subtitle() }}</p>
              }
              @if (errored()) {
                <p class="error">{{ 'tlc.tellent_assistant.proposal.error' | translate }}</p>
              }
              <div class="actions">
                <rt-button variant="plain" [disabled]="busy()" (click)="skipped.emit()">
                  {{ 'tlc.tellent_assistant.proposal.skip' | translate }}
                </rt-button>
                <rt-button variant="primary" [disabled]="busy()" (click)="confirmed.emit()">
                  {{ yesLabel() }}
                </rt-button>
              </div>
            }
          }
        </footer>
      }
    </section>

    @if (!auto() && resolvedStatus() === 'confirmed') {
      <div class="success-banner">
        <rt-icon class="banner-icon" name="check" />
        <span class="banner-text">{{ doneText() }}</span>
        @if (resultLink(); as link) {
          @if (link.target; as linkTarget) {
            <a
              class="banner-link"
              [tellentAssistantLink]="linkTarget"
              target="_blank"
              rel="noopener noreferrer"
              [attr.aria-label]="link.label"
            >
              <rt-icon name="open-in-new" />
            </a>
          } @else {
            <a
              class="banner-link"
              [href]="link.url"
              target="_blank"
              rel="noopener noreferrer"
              [attr.aria-label]="link.label"
            >
              <rt-icon name="open-in-new" />
            </a>
          }
        }
      </div>
    }
  `,
  styleUrl: './proposal-preview.component.less',
})
export class TellentAssistantProposalPreviewComponent {
  private readonly i18n = inject(I18nService);

  private readonly argsRecord = computed<Record<string, unknown>>(() => {
    const value = this.args();
    return typeof value === 'object' && value !== null ? <Record<string, unknown>>value : {};
  });

  protected readonly label = computed(
    () => this.confirm()?.preview_label ?? this.confirm()?.title ?? '',
  );

  // The contract carries a plain string; unknown names fail soft (no glyph).
  protected readonly icon = computed(() => <IconName>(this.confirm()?.icon ?? 'description'));

  protected readonly properties = computed<DisplayProperty[]>(() =>
    buildDisplayProperties(this.display(), this.argsRecord()),
  );

  protected readonly question = computed(
    () =>
      this.confirm()?.question ??
      this.i18n.translate('tlc.tellent_assistant.proposal.confirm_fallback'),
  );

  protected readonly subtitle = computed(
    () => this.confirm()?.summary ?? this.confirm()?.effect ?? '',
  );

  protected readonly yesLabel = computed(
    () =>
      this.confirm()?.yes_label ?? this.i18n.translate('tlc.tellent_assistant.proposal.confirm'),
  );

  protected readonly approvedText = computed(
    () =>
      this.confirm()?.approved_label ??
      this.i18n.translate('tlc.tellent_assistant.proposal.approved'),
  );

  protected readonly doneText = computed(
    () =>
      this.display()?.done ??
      this.confirm()?.done_label ??
      this.i18n.translate('tlc.tellent_assistant.proposal.done_fallback'),
  );

  public readonly args = input<unknown>();
  /** Model-authored presentation of the proposal in user terms (names, not ids). */
  public readonly display = input<ProposeActionDisplay | null>(null);
  public readonly confirm = input<ProposeActionConfirm | null>(null);
  /** Link to the created/affected resource; present once the action executed. */
  public readonly resultLink = input<ProposalResultLink | null>(null);
  /** How this proposal was resolved; `null` while the decision is pending. */
  public readonly resolvedStatus = input<'confirmed' | 'cancelled' | null>(null);
  /** A later user turn moved on: a still-pending card drops its footer so it can't fire. */
  public readonly superseded = input<boolean>(false);
  /** True for auto-policy actions the agent already executed (receipt mode). */
  public readonly auto = input<boolean>(false);
  /** True while this proposal's confirmation is being dispatched. */
  public readonly busy = input<boolean>(false);
  /** True when the last confirmation attempt failed (the card stays actionable). */
  public readonly errored = input<boolean>(false);

  public readonly confirmed = output<void>();
  public readonly skipped = output<void>();
}
